import * as React from 'react'
import { STATUS_COLORS } from '../../../constants/statusColors'
import { KanbanCard } from './KanbanCard'
import type { SessionSummary, StatusColumnConfig } from './types'

interface KanbanColumnProps {
  column: StatusColumnConfig
  sessions: SessionSummary[]
  columns: StatusColumnConfig[]
  currentPage: number
  pageSize: number
  recentlyMoved: Record<string, string>
  statusUpdating: string | null
  onStatusChange: (sessionId: string, newStatus: string) => void
  onNavigate: (session: SessionSummary) => void
  onLoadMore: (columnId: string) => void
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  sessions,
  columns,
  currentPage,
  pageSize,
  recentlyMoved,
  statusUpdating,
  onStatusChange,
  onNavigate,
  onLoadMore,
}) => {
  const visible = sessions.slice(0, currentPage * pageSize)
  const hasMore = sessions.length > visible.length
  const columnTheme = STATUS_COLORS[column.id as keyof typeof STATUS_COLORS]

  const allowDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const sessionId = e.dataTransfer.getData('text/plain')
    if (!sessionId) return
    const draggedStatus = e.dataTransfer.getData('status')
    if (draggedStatus === column.id) return
    onStatusChange(sessionId, column.id)
  }

  return (
    <div
      className={`rounded-2xl border ${column.tone} p-4 space-y-3`}
      onDragOver={allowDrop}
      onDrop={handleDrop}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex h-2.5 w-2.5 rounded-full ${
              columnTheme?.dot || 'bg-gray-400'
            }`}
          />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {column.label}
          </h3>
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-white/70 text-gray-700 dark:bg-gray-800 dark:text-gray-200 border">
          {sessions.length}
        </span>
      </div>

      <div className="space-y-3">
        {visible.map(session => (
          <KanbanCard
            key={session.id}
            session={session}
            columns={columns}
            justMoved={!!recentlyMoved[session.id]}
            statusUpdating={statusUpdating}
            onNavigate={onNavigate}
            onStatusChange={onStatusChange}
          />
        ))}
        {visible.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nenhuma conversa aqui.
          </p>
        )}
      </div>

      {hasMore && (
        <button
          onClick={() => onLoadMore(column.id)}
          className={`w-full text-xs px-3 py-2 rounded-lg border text-gray-900 dark:text-white transition ${
            columnTheme?.activeBg || 'bg-gray-50'
          } ${columnTheme?.activeBorder || 'border-gray-200'} ${
            columnTheme?.text || ''
          } hover:opacity-90`}
        >
          Carregar mais
        </button>
      )}
    </div>
  )
}
